import {html, LitElement} from 'lit';
import {DomModule} from '@polymer/polymer/lib/elements/dom-module.js';
import '@cbar/cbar-shared-styles';
import '@material/web/dialog/dialog.js';
import '@cbar/cbar-address-autocomplete/cbar-address-autocomplete.js';
import '@polymer/paper-button/paper-button.js';
import '@polymer/paper-spinner/paper-spinner.js';
import '@polymer/iron-icons/iron-icons.js';
import '@cbar/cbar-textfield';
import "@cbar/cbar-dropdown";
import '@polymer/paper-icon-button/paper-icon-button.js';
import {LitLocalize} from "@cbar/lit-localize";

const themeTemplate = DomModule.import('cbar-shared-styles', 'template');
const sharedThemeElement = themeTemplate?.content?.firstElementChild;

if (!sharedThemeElement) {
  console.warn('The theme template or its first element child is missing.');
}

const sharedTheme = sharedThemeElement?.textContent ?? '';

export class CbarLocationModal extends LitLocalize(LitElement) {
  static get properties() {
    return {
      title: String,
      streetAddress1: String,
      streetAddress2: String,
      streetAddress3: String,
      state: String,
      suburb: String,
      country: String,
      postcode: String,
      headerStatement: String,
      submitStatement: String,
      apiKey: String,
      loading: Boolean,
      singleLine: {
        type: Boolean,
        reflect: true,
      },
      hideDelete: Boolean,
      value: String,
      hideLabel: Boolean,
      inlineLabel: {
        type: Boolean,
        reflect: true,
      },
      locationType: String,
      customLocationType: String,
      isCustomLocationType: Boolean,
      locationTypes: Array,
      hasAddress: {
        attribute: 'has-address',
        type: Boolean,
        reflect: true,
      },
      isNewEntry: Boolean,
      inputType: String,
      opened: {
        type: Boolean,
        reflect: true,
      },
      editUrl: {
        type: String,
        value: "",
      }
    };
  }

  constructor() {
    super();
    this.headerStatement = "";
    this.submitStatement = "";
    this.apiKey = "";
    this.title = "";
    this.streetAddress1 = "";
    this.streetAddress2 = "";
    this.streetAddress3 = "";
    this.state = "";
    this.suburb = "";
    this.country = "";
    this.postcode = "";
    this.loading = false;
    this.singleLine = false;
    this.hideDelete = false;
    this.locationType = "";

    this.locationTypes = ['home', 'work', 'other'];
  }

  render() {
    return html`
      <style>
        ${sharedTheme}
        :host {
          font-family: Roboto, noto, sans-serif;
        }

        :host([inlinelabel]) .label-container {
          display: flex;
          align-items: center;
        }

        :host([inlinelabel]) .label {
          margin-right: 8px;
        }

        :host([singleline]) #clickable {
          white-space: normal;
        }

        :host(:not([singleline])) #clickable {
          white-space: pre;
        }

        #clickable {
          text-decoration: none;
          color: var(--cbar-sys-color-secondary, var(--cbar-dark-primary-color, #003F83));
          cursor: pointer;
        }

        cbar-dropdown, cbar-address-autocomplete, cbar-textfield {
          --mdc-theme-primary: var(--cbar-sys-color-secondary, var(--cbar-dark-primary-color, #003F83));
          --cbar-sys-color-primary: var(--cbar-sys-color-secondary, var(--cbar-dark-primary-color, #003F83));
        }

        md-dialog {
          width: 40%;
          max-width: 800px;
        }

        .buttons {
          display: flex;
          align-items: center;
        }

        cbar-address-autocomplete {
          width: 100%;
        }

        .wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        paper-icon-button {
          min-width: 40px;
          min-height: 40px;
          --paper-icon-button: {
            color: var(--cbar-secondary-text-color, black)
          };
        }

        @media (max-width: 700px) {
          md-dialog {
            width: 100%;
            max-width: unset;
          }
        }
      </style>
      <div class="wrapper">
        <div class="label-container">
          <div .hidden="${this.hideLabel}" class="label">${this.localize(this.__displayedLocationType)}</div>

          <div id="clickable" part="link" @click="${(e) => {this.toggleModal(e)}}" @keyup="${(e) => {this.toggleModal(e)}}">
            ${this.hasAddress ? this.getFormattedLocationString() : this.localize("Click here to enter")}
          </div>
        </div>

        ${this.hideDelete ? html`` : html`
          <paper-icon-button icon="delete" @tap="${(e) => {this.__handleDelete(e)}}"></paper-icon-button>`}
      </div>

      <md-dialog  .opened=${this.opened} @close=${e => {this.__closeHandler(e)}}>
        <h6 slot="headline">${this.headerStatement}</h6>
        <div slot="content">
          <cbar-dropdown dense label="${this.localize("Type")}" .items=${this.formattedLocationTypes} @change=${e => this.onSelectedLocationTypeChanged(e)} .value=${this.selectedLocationType ? this.selectedLocationType.id : null}></cbar-dropdown>
          <cbar-textfield
            style="width: 100%; margin-top: 8px"
            id="customLocationType"
            label="${this.localize('Custom Type')}"
            value="${this.customLocationType}"
            @input=${(e) => this.onCustomLocationTypeChanged(e)}
            @blur="${(e) => this.setCustomRoleValidity(!e.target.reportValidity())}"
            ?hidden="${!this.isCustomLocationType}"
            required
            dense
          ></cbar-textfield>

          <cbar-address-autocomplete api-key="${this.apiKey}" street-address="${this.streetAddress1}" street-address-2="${this.streetAddress2}" street-address3="${this.streetAddress3}" suburb="${this.suburb}" title="${this.title}" state="${this.state}" country="${this.country}" postcode="${this.postcode}"></cbar-address-autocomplete>
        </div>
        <div class="buttons" slot="actions">
          <paper-spinner></paper-spinner>
          <paper-button @tap="${() => {this.__handleCancel()}}">${this.localize('Cancel')}</paper-button>
          <paper-button @tap="${(e) => {this.__updateLocation(e)}}">${this.submitStatement}</paper-button>
        </div>
      </md-dialog>
    `;
  }

  willUpdate(changedProps) {
    this.value = this.getFormattedLocationString();

    this.hasAddress = this.__hasValidAddress();

    if (changedProps.has('locationTypes')) {
      this.formattedLocationTypes = this.locationTypes.map(locationType => ({
        id: locationType,
        title: locationType
      }));
    }

    if (changedProps.has('locationType') || changedProps.has('locationTypes')) {
      this._updateSelectedLocationType();
    }

    if (changedProps.has('hasAddress')) {
      this.isNewEntry = !this.hasAddress;
      const verb = this.hasAddress ? 'Edit' : 'Add';

      this.headerStatement = this.localize(`${verb} Location`);
      this.submitStatement = this.localize(verb);

      this.isCustomLocationType = this.locationType === "other";
      this.__displayedLocationType = this.isCustomLocationType ? this.customLocationType : this.locationType
    }
  }

  get customLocationInputElement() {
    return this.shadowRoot.querySelector('#customLocationType')
  }

  get modal() {
    if (!this.__modal) {
      this.__modal = this.shadowRoot.querySelector('md-dialog');
    }

    return this.__modal;
  }

  get form() {
    if (!this.__form) {
      this.__form = this.shadowRoot.querySelector('cbar-address-autocomplete');
    }

    return this.__form;
  }

  get spinner() {
    if (!this.__spinner) {
      this.__spinner = this.shadowRoot?.querySelector('paper-spinner');
    }

    return this.__spinner;
  }

  get buttons() {
    if (!this.__buttons || this.__buttons.length === 0) {
      this.__buttons = this.shadowRoot.querySelectorAll('paper-button');
    }

    return this.__buttons;
  }


  get headerStatement() {
    return this._headerStatement;
  }

  get hideDelete() {
    return this._hideDelete;
  }

  get submitStatement() {
    return this._submitStatement;
  }

  get apiKey() {
    return this._apiKey;
  }

  get title() {
    return this._title;
  }

  get streetAddress1() {
    return this._streetAddress1;
  }

  get streetAddress2() {
    return this._streetAddress2;
  }

  get streetAddress3() {
    return this._streetAddress3;
  }

  get state() {
    return this._state;
  }

  get suburb() {
    return this._suburb;
  }

  get country() {
    return this._country;
  }

  get postcode() {
    return this._postcode;
  }

  get locationType() {
    return this._locationType;
  }

  get loading() {
    return this._loading;
  }

  set title(title) {
    this._title = title;
    this.requestUpdate();
  }

  set streetAddress1(streetAddress) {
    this._streetAddress1 = streetAddress;
    this.requestUpdate();
  }

  set streetAddress2(streetAddress2) {
    this._streetAddress2 = streetAddress2;
    this.requestUpdate();
  }

  set streetAddress3(streetAddress3) {
    this._streetAddress3 = streetAddress3;
    this.requestUpdate();
  }

  set state(state) {
    this._state = state;
    this.requestUpdate();
  }

  set suburb(suburb) {
    this._suburb = suburb;
    this.requestUpdate();
  }

  set country(country) {
    this._country = country;
    this.requestUpdate();
  }

  set postcode(postcode) {
    this._postcode = postcode;
    this.requestUpdate();
  }

  set apiKey(key) {
    this._apiKey = key;
    this.requestUpdate();
  }

  set locationType(type) {
    this._locationType = type;
    this.requestUpdate();
  }

  set headerStatement(statement) {
    this._headerStatement = statement;
    this.requestUpdate();
  }

  set submitStatement(statement) {
    this._submitStatement = statement;
    this.requestUpdate();
  }

  set hideDelete(hide) {
    this._hideDelete = hide;
    this.requestUpdate();
  }

  set loading(loading) {
    this._loading = loading;
    this.__setLoadingStatus(loading);
    this.requestUpdate();
  }

  toggle() {
    if (!this.opened) {
      // When opening to edit, store our locationType in case of cancellation
      this._initialLocationType = this.locationType;
      this._initialCustomLocationType = this.customLocationType;
    }

    this.opened ? this.modal.close() : this.modal.show();
    this.opened = !this.opened;
  }

  toggleModal() {
    if (this.editUrl) {
      window.locaton.href = this.editUrl;
      return;
    }

    this.toggle();
  }

  __updateLocation() {
    if (!this.validate()) {
      return;
    }

    this.loading = true;

    this.__updateAddressParams();

    const addressChangedEvent = this.dispatchEvent(new CustomEvent('address-changed', {
      composed: true,
      bubbles: true,
      cancelable: true,
      detail: {
        address: {
          title: this.title,
          streetAddress1: this.streetAddress1,
          streetAddress2: this.streetAddress2,
          streetAddress3: this.streetAddress3,
          state: this.state,
          suburb: this.suburb,
          country: this.country,
          postcode: this.postcode,
        },
        addressString: this.getFormattedLocationString()
      }
    }));

    if (!addressChangedEvent.defaultPrevented) {
      this.resetForm();
      this.loading = false;
      this.toggle();
    }
  }

  __hasValidAddress() {
    const requiredParams = [
      this.streetAddress1,
      this.suburb,
      this.country,
      this.state,
      this.postcode
    ];

    return requiredParams.every(param => param);
  }

  __updateAddressParams() {
    this.__displayedLocationType = this.isCustomLocationType ? this.customLocationType : this.locationType;

    this.title = this.form.title;
    this.streetAddress1 = this.form.streetAddress;
    this.streetAddress2 = this.form.streetAddress2;
    this.streetAddress3 = this.form.streetAddress3;
    this.state = this.form.state;
    this.suburb = this.form.suburb;
    this.country = this.form.country;
    this.postcode = this.form.postcode;
  }

  resetForm() {
    this.__setLoadingStatus(false);

    this.form.title = this.title;
    this.form.streetAddress = this.streetAddress1;
    this.form.streetAddress2 = this.streetAddress2;
    this.form.streetAddress3 = this.streetAddress3;
    this.form.state = this.state;
    this.form.suburb = this.suburb;
    this.form.country = this.country;
    this.form.postcode = this.postcode;
  }

  getFormattedLocationString() {
    const groupedParts = [
      this.title,
      this.streetAddress1,
      this.streetAddress2,
      this.streetAddress3,
      [
        this.suburb,
        this.state,
        this.postcode,
      ],
      this.country,
    ];

    const separator = this.singleLine ? ', ' : '\n';

    return groupedParts.reduce((formatted, part) => {
      let str = part;
      if (Array.isArray(str)) {
        // Grouped parts are joined together by a space
        str = str.join(' ');
      }

      if (str) {
        formatted.push(str);
      }

      return formatted;
    }, []).join(separator);
  }

  /* eslint-disable no-param-reassign */
  __setLoadingStatus(loading) {
    if (!this.spinner || !this.buttons) {
      return;
    }

    this.spinner.active = loading;
    this.buttons.forEach(button => {
      button.disabled = loading;
    });
  }

  validate() {
    const customValueValid = !this.isCustomLocationType || (this.isCustomLocationType && this.customLocationInputElement.reportValidity());
    this.setCustomRoleValidity(!customValueValid);

    return customValueValid && this.form.validate();
  }

  __handleCancel() {
    this.resetForm();
    this.locationType = this._initialLocationType;
    this.customLocationType = this._initialCustomLocationType;

    this.toggle();

    this.dispatchEvent(new CustomEvent('entry-cancelled', {
      bubbles: true,
      composed: true
    }));
  }

  __handleDelete() {
    this.dispatchEvent(new CustomEvent('entry-deleted', {
      bubbles: true,
      composed: true
    }));
  }

  onSelectedLocationTypeChanged(e) {
    const newLocationType = e.target.value;

    if (!newLocationType) {
      return;
    }

    const customEvent = new CustomEvent('location-type-changed', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {value: newLocationType},
    });
    this.dispatchEvent(customEvent);

    if (customEvent.defaultPrevented) {
      return;
    }

    this.locationType = newLocationType;

    this.isCustomLocationType = newLocationType === "other";
  }

  onCustomLocationTypeChanged(e) {
    const input = e.target;
    this.customLocationType = input.value;
  }

  _updateSelectedLocationType() {
    if (!this.locationType) {
      this.selectedLocationType = null;

      return;
    }

    this.selectedLocationType = this.formattedLocationTypes.find(lt => lt.title === this.locationType);
  }

  setCustomRoleValidity(invalid) {
    this.customLocationInputElement.validationMessage = invalid ? this.localize('Required') : '';
  }

  __closeHandler(e) {
    if (this.loading) {
      e.preventDefault()
    } else {
      this.opened = false;
    }
  }
}
